import { useEffect, useState } from "react";
import { Copy, Download, ImageIcon, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { wrap } from "./MilestoneCard";
import { currentSeasonIndex } from "./SeasonalHero";

export type V = { ref: string; text: string; version: string; footer?: string; eyebrow?: string; kind?: string; note?: string };
export const LAKE = "https://static.prod-images.emergentagent.com/jobs/2bfd65d8-4762-4154-9373-440077215f5a/images/08ceb7feaca809d95ded7ecbbf68066d46d118bb35c97f43b58c7f3e03534bab.jpeg";
export const SEASON = [["#F4B6C2", "#81B29A", "#2b3a4a"], ["#F2A541", "#3D7EA6", "#1E293B"], ["#C86D51", "#D97706", "#2a1c17"], ["#5E7CA6", "#B5C7DA", "#0b1222"]];
export const CARD_STYLES = [{ id: "lake", label: "Painted lake" }, { id: "season", label: "Seasonal" }, { id: "parchment", label: "Parchment" }];
const W = 1080, H = 1350;

const loadImg = (src: string) => new Promise<HTMLImageElement>((res, rej) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = rej; i.src = src; });

function drawAffirm(x: CanvasRenderingContext2D, v: V) {
  const g = x.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#1B1F4B"); g.addColorStop(0.45, "#6B3A6E"); g.addColorStop(0.75, "#E07A5F"); g.addColorStop(1, "#F6C177");
  x.fillStyle = g; x.fillRect(0, 0, W, H);
  const cx = W / 2, cy = H + 60;
  x.save(); x.translate(cx, cy);
  for (let i = 0; i < 24; i++) {
    x.rotate((Math.PI * 2) / 24);
    x.fillStyle = i % 2 ? "rgba(255,236,190,0.10)" : "rgba(255,236,190,0.05)";
    x.beginPath(); x.moveTo(0, 0); x.lineTo(-60, -1500); x.lineTo(60, -1500); x.closePath(); x.fill();
  }
  x.restore();
  const sun = x.createRadialGradient(cx, cy, 40, cx, cy, 380); sun.addColorStop(0, "rgba(255,240,200,1)"); sun.addColorStop(0.5, "rgba(255,214,140,0.9)"); sun.addColorStop(1, "rgba(255,214,140,0)");
  x.fillStyle = sun; x.beginPath(); x.arc(cx, cy, 380, 0, Math.PI * 2); x.fill();
  for (let i = 0; i < 60; i++) { x.fillStyle = `rgba(255,255,255,${0.25 + ((i * 17) % 10) / 20})`; x.beginPath(); x.arc((i * 397) % W, (i * 211) % (H * 0.45), 1.2 + (i % 3), 0, Math.PI * 2); x.fill(); }
  x.textAlign = "center";
  x.fillStyle = "#FDE7C2"; x.font = "600 26px Manrope, sans-serif"; x.fillText(v.eyebrow || "TODAY'S AFFIRMATION", cx, 190);
  x.fillRect(cx - 60, 220, 120, 3);
  const size = v.text.length > 120 ? 64 : v.text.length > 70 ? 76 : 88;
  x.font = `700 ${size}px 'Cormorant Garamond', serif`;
  const lines = wrap(x, v.text, W - 200).slice(0, 9);
  const lh = size * 1.18, top = Math.max(330, (H * 0.82 - lines.length * lh) / 2 + 60);
  x.fillStyle = "#FFFFFF"; x.shadowColor = "rgba(20,10,40,.45)"; x.shadowBlur = 18;
  lines.forEach((l, i) => x.fillText(l, cx, top + i * lh));
  x.shadowBlur = 0;
  x.fillStyle = "#FDE7C2"; x.font = "600 34px Manrope, sans-serif"; x.fillText(v.footer || v.ref, cx, top + lines.length * lh + 40);
  x.fillStyle = "#3B1F2B"; x.font = "700 48px 'Cormorant Garamond', serif"; x.fillText("Cast", cx, H - 70);
}

async function drawVerse(v: V, style: string) {
  await document.fonts.ready;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const x = cv.getContext("2d")!;
  if (style === "affirm") { drawAffirm(x, v); return cv; }
  let ink = "#FFFFFF", accent = "#F5C98B";
  if (style === "lake") {
    try { const img = await loadImg(LAKE); const s = Math.max(W / img.width, H / img.height); x.drawImage(img, (W - img.width * s) / 2, (H - img.height * s) / 2, img.width * s, img.height * s); }
    catch { x.fillStyle = "#1E293B"; x.fillRect(0, 0, W, H); }
    const g = x.createLinearGradient(0, 0, 0, H); g.addColorStop(0, "rgba(15,23,42,0.35)"); g.addColorStop(0.5, "rgba(15,23,42,0.55)"); g.addColorStop(1, "rgba(15,23,42,0.85)");
    x.fillStyle = g; x.fillRect(0, 0, W, H);
  } else if (style === "season") {
    const [a, b, dark] = SEASON[currentSeasonIndex()];
    const g = x.createLinearGradient(0, 0, W, H); g.addColorStop(0, dark); g.addColorStop(0.6, a); g.addColorStop(1, b);
    x.fillStyle = g; x.fillRect(0, 0, W, H);
    x.fillStyle = "rgba(15,23,42,0.35)"; x.fillRect(0, 0, W, H);
    for (let i = 0; i < 40; i++) { const r = 6 + ((i * 37) % 14); x.fillStyle = `rgba(255,255,255,${0.08 + ((i * 13) % 10) / 60})`; x.beginPath(); x.ellipse((i * 263) % W, (i * 541) % H, r, r * 0.6, i, 0, Math.PI * 2); x.fill(); }
  } else {
    x.fillStyle = "#FBF4E6"; x.fillRect(0, 0, W, H);
    const g = x.createRadialGradient(W / 2, H / 2, 200, W / 2, H / 2, 900); g.addColorStop(0, "rgba(255,255,255,0)"); g.addColorStop(1, "rgba(180,140,90,0.28)");
    x.fillStyle = g; x.fillRect(0, 0, W, H);
    x.strokeStyle = "#B7791F"; x.lineWidth = 4; x.strokeRect(48, 48, W - 96, H - 96); x.lineWidth = 1.5; x.strokeRect(66, 66, W - 132, H - 132);
    ink = "#3B2A1A"; accent = "#B7791F";
  }
  x.fillStyle = ink;
  x.font = "700 56px 'Cormorant Garamond', serif"; x.fillText("Cast", 100, 150);
  x.fillStyle = accent; x.font = "600 24px Manrope, sans-serif"; x.fillText(v.eyebrow || "A VERSE I'M HOLDING ONTO", 102, 192);
  const size = Math.min(v.text.length, 600) > 320 ? 40 : v.text.length > 200 ? 48 : v.text.length > 110 ? 56 : 66;
  x.font = `italic ${size}px 'EB Garamond', serif`;
  const body = v.text.length > 600 ? v.text.slice(0, 580).trimEnd() + "…" : v.text;
  const lines = wrap(x, `“${body}”`, W - 220).slice(0, v.note ? 11 : 16);
  const lh = size * 1.38, top = Math.max(280, (H - lines.length * lh - (v.note ? 260 : 0)) / 2);
  x.fillStyle = ink; lines.forEach((l, i) => x.fillText(l, 100, top + i * lh));
  x.fillStyle = accent; x.font = "600 34px Manrope, sans-serif"; x.fillText(v.footer || `${v.ref} · ${v.version}`, 102, top + lines.length * lh + 30);
  if (v.note) {
    x.font = "500 30px Manrope, sans-serif";
    const nl = wrap(x, v.note.length > 220 ? v.note.slice(0, 210).trimEnd() + "…" : v.note, W - 260).slice(0, 4);
    const ny = H - 110 - nl.length * 42;
    x.fillStyle = style === "parchment" ? "rgba(183,121,31,0.12)" : "rgba(255,255,255,0.14)";
    x.fillRect(84, ny - 70, W - 168, nl.length * 42 + 60);
    x.fillStyle = accent; x.font = "700 22px Manrope, sans-serif"; x.fillText("MY NOTE", 110, ny - 32);
    x.fillStyle = ink; x.font = "500 30px Manrope, sans-serif"; nl.forEach((l, i) => x.fillText(l, 110, ny + 8 + i * 42));
  }
  return cv;
}

export function VerseCardDialog({ verse, open, onOpenChange, initialStyle = "lake", styles, noteText }: { verse: V; open: boolean; onOpenChange: (o: boolean) => void; initialStyle?: string; styles?: string[]; noteText?: string }) {
  const [style, setStyle] = useState(initialStyle);
  const [url, setUrl] = useState("");
  const [blob, setBlob] = useState<Blob | null>(null);
  const [withNote, setWithNote] = useState(false);
  useEffect(() => { if (open) setWithNote(false); }, [open]);
  useEffect(() => {
    if (!open) return;
    setUrl("");
    let live = true;
    drawVerse(withNote && noteText ? { ...verse, note: noteText } : verse, style).then((cv) => { if (!live) return; setUrl(cv.toDataURL("image/png")); cv.toBlob((b) => live && setBlob(b), "image/png"); });
    return () => { live = false; };
  }, [open, style, verse, withNote, noteText]);
  const name = `cast-${verse.ref.replace(/\W+/g, "-").toLowerCase()}.png`;
  const text = verse.footer ? `“${verse.text}” ${verse.footer}` : `“${verse.text}” (${verse.ref}, ${verse.version})`;
  const file = () => new File([blob!], name, { type: "image/png" });
  const share = async () => {
    try {
      if (blob && navigator.canShare?.({ files: [file()] })) await navigator.share({ files: [file()], title: verse.ref, text });
      else if (navigator.share) await navigator.share({ title: verse.ref, text });
      else { await navigator.clipboard.writeText(text); toast.success("Verse copied to share"); }
    } catch (e: any) { if (e?.name !== "AbortError") toast.error("Sharing isn't available here. Try Save instead."); }
  };
  const download = () => { const a = document.createElement("a"); a.href = url; a.download = name; a.click(); };
  const copy = async () => { try { await navigator.clipboard.writeText(text); toast.success("Verse copied"); } catch { toast.error("Couldn't copy"); } };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto" data-testid="verse-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-3xl"><ImageIcon className="h-6 w-6 text-amber-600" />{verse.kind || "Verse"} card</DialogTitle>
          <DialogDescription>{styles?.length === 1 ? "Share or save your picture." : "Pick a style, then share or save your picture."}</DialogDescription>
        </DialogHeader>
        {!(styles?.length === 1) && <div className="flex gap-2" data-testid="verse-card-styles">
          {CARD_STYLES.filter((s) => !styles || styles.includes(s.id)).map((s) => (
            <button key={s.id} data-testid={`verse-card-style-${s.id}`} onClick={() => setStyle(s.id)} aria-pressed={style === s.id}
              className={`flex-1 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${style === s.id ? "bg-[#1E293B] text-white" : "bg-[#F3ECE0] text-slate-700 hover:bg-[#E9DFCF]"}`}>{s.label}</button>
          ))}
        </div>}
        {noteText && (
          <label className="flex items-center justify-between gap-3 rounded-xl bg-[#FBF7EF] px-4 py-2.5 text-sm font-semibold text-slate-700" data-testid="verse-card-note-toggle-row">
            Add my note to the card
            <Switch data-testid="verse-card-note-toggle" checked={withNote} onCheckedChange={setWithNote} />
          </label>
        )}
        {url ? <img src={url} alt={`Verse card for ${verse.ref}`} className="w-full rounded-2xl shadow-xl" data-testid="verse-card-image" data-style={style} />
          : <div className="aspect-[4/5] w-full animate-pulse rounded-2xl bg-[#F3ECE0]" />}
        <div className="grid grid-cols-3 gap-2">
          <Button data-testid="verse-card-share" onClick={share} disabled={!url} className="rounded-full bg-[#C86D51] hover:bg-[#B35A3F]"><Share2 className="mr-1.5 h-4 w-4" />Share</Button>
          <Button data-testid="verse-card-download" onClick={download} disabled={!url} variant="outline" className="rounded-full"><Download className="mr-1.5 h-4 w-4" />Save</Button>
          <Button data-testid="verse-card-copy" onClick={copy} variant="outline" className="rounded-full"><Copy className="mr-1.5 h-4 w-4" />Copy</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
